interface LoginCredentials {
  username: string;
  password: string;
}

interface AuthUser {
  username: string;
  userType: 'superadmin' | 'manager';
  firstName?: string;
  lastName?: string;
  isAuthenticated: boolean;
  loginTime: number;
}

interface ManagerAccount {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  userType: 'manager';
  createdAt: number;
}

interface TimeEntry {
  id: string;
  username: string;
  clockIn: number;
  clockOut?: number;
  date: string; // YYYY-MM-DD format
}

// API configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Use relative URLs in production
  : 'http://localhost:8080';

class AuthService {
  private readonly STORAGE_KEY = 'alphoric_auth';
  
  // Session timeout: 24 hours (in milliseconds)
  private readonly SESSION_TIMEOUT = 24 * 60 * 60 * 1000;

  /**
   * Create a new manager account via API
   */
  async createManagerAccount(accountData: {
    username: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/managers/create-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(accountData)
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to create account' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error creating manager account:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /**
   * Authenticate user with provided credentials via API
   */
  async login(credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/managers/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials)
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Login failed' };
      }

      // Store auth data in localStorage
      const authData: AuthUser = {
        username: result.data.username,
        userType: result.data.userType,
        firstName: result.data.firstName,
        lastName: result.data.lastName,
        isAuthenticated: true,
        loginTime: Date.now()
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(authData));
      return { success: true };

    } catch (error) {
      console.error('Error during login:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /**
   * Log out the current user
   */
  logout(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    try {
      const authDataString = localStorage.getItem(this.STORAGE_KEY);
      
      if (!authDataString) {
        return false;
      }

      const authData: AuthUser = JSON.parse(authDataString);
      
      // Check if session has expired
      if (Date.now() - authData.loginTime > this.SESSION_TIMEOUT) {
        this.logout(); // Clean up expired session
        return false;
      }

      return authData.isAuthenticated;
    } catch (error) {
      console.error('Error checking authentication:', error);
      this.logout(); // Clean up corrupted data
      return false;
    }
  }

  /**
   * Get current authenticated user info
   */
  getCurrentUser(): AuthUser | null {
    try {
      const authDataString = localStorage.getItem(this.STORAGE_KEY);
      
      if (!authDataString) {
        return null;
      }

      const authData: AuthUser = JSON.parse(authDataString);
      
      // Check if session has expired
      if (Date.now() - authData.loginTime > this.SESSION_TIMEOUT) {
        this.logout();
        return null;
      }

      return authData.isAuthenticated ? authData : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      this.logout();
      return null;
    }
  }

  /**
   * Extend current session (refresh login time)
   */
  refreshSession(): void {
    try {
      const authDataString = localStorage.getItem(this.STORAGE_KEY);
      
      if (!authDataString) {
        return;
      }

      const authData: AuthUser = JSON.parse(authDataString);
      
      if (authData.isAuthenticated) {
        authData.loginTime = Date.now();
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(authData));
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
    }
  }

  /**
   * Get session time remaining in milliseconds
   */
  getSessionTimeRemaining(): number {
    try {
      const authDataString = localStorage.getItem(this.STORAGE_KEY);
      
      if (!authDataString) {
        return 0;
      }

      const authData: AuthUser = JSON.parse(authDataString);
      const timeElapsed = Date.now() - authData.loginTime;
      const timeRemaining = this.SESSION_TIMEOUT - timeElapsed;
      
      return Math.max(0, timeRemaining);
    } catch (error) {
      console.error('Error getting session time remaining:', error);
      return 0;
    }
  }

  // Time tracking functionality via API

  /**
   * Clock in current user
   */
  async clockIn(): Promise<{ success: boolean; error?: string; clockInTime?: number }> {
    try {
      const user = this.getCurrentUser();
      if (!user || user.userType !== 'manager') {
        return { success: false, error: 'Only managers can clock in' };
      }

      const response = await fetch(`${API_BASE_URL}/api/managers/clock-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: user.username })
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to clock in' };
      }

      return { success: true, clockInTime: result.data.clockInTime };
    } catch (error) {
      console.error('Error clocking in:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /**
   * Clock out current user
   */
  async clockOut(): Promise<{ success: boolean; error?: string; clockOutTime?: number }> {
    try {
      const user = this.getCurrentUser();
      if (!user || user.userType !== 'manager') {
        return { success: false, error: 'Only managers can clock out' };
      }

      const response = await fetch(`${API_BASE_URL}/api/managers/clock-out`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: user.username })
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to clock out' };
      }

      return { success: true, clockOutTime: result.data.clockOutTime };
    } catch (error) {
      console.error('Error clocking out:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /**
   * Get current clock status for user
   */
  async getCurrentClockStatus(): Promise<{ isClockedIn: boolean; lastClockIn?: number; lastClockOut?: number }> {
    try {
      const user = this.getCurrentUser();
      if (!user || user.userType !== 'manager') {
        return { isClockedIn: false };
      }

      const response = await fetch(`${API_BASE_URL}/api/managers/${user.username}/clock-status`);
      const result = await response.json();

      if (!response.ok) {
        console.error('Failed to get clock status:', result.error);
        return { isClockedIn: false };
      }

      return result.data;
    } catch (error) {
      console.error('Error getting clock status:', error);
      return { isClockedIn: false };
    }
  }

  /**
   * Get time entries for current user or all users (super admin only)
   */
  async getTimeEntries(username?: string): Promise<TimeEntry[]> {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) return [];

      const targetUsername = username || currentUser.username;
      
      // Only super admin can view other users' time
      if (currentUser.userType !== 'superadmin' && targetUsername !== currentUser.username) {
        return [];
      }

      const response = await fetch(`${API_BASE_URL}/api/managers/${targetUsername}/time-entries`);
      const result = await response.json();

      if (!response.ok) {
        console.error('Failed to get time entries:', result.error);
        return [];
      }

      return result.data;
    } catch (error) {
      console.error('Error getting time entries:', error);
      return [];
    }
  }

  /**
   * Get aggregated daily time for a user
   */
  async getDailyAggregatedTime(date: string, username?: string): Promise<{ totalMinutes: number; entries: TimeEntry[] }> {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) return { totalMinutes: 0, entries: [] };

      const targetUsername = username || currentUser.username;
      
      // Only super admin can view other users' time
      if (currentUser.userType !== 'superadmin' && targetUsername !== currentUser.username) {
        return { totalMinutes: 0, entries: [] };
      }

      const response = await fetch(`${API_BASE_URL}/api/managers/${targetUsername}/time-entries?date=${date}`);
      const result = await response.json();

      if (!response.ok) {
        console.error('Failed to get daily time entries:', result.error);
        return { totalMinutes: 0, entries: [] };
      }

      const entries = result.data;
      let totalMinutes = 0;

      entries.forEach((entry: TimeEntry) => {
        if (entry.clockOut) {
          const minutes = (entry.clockOut - entry.clockIn) / (1000 * 60);
          totalMinutes += minutes;
        }
      });

      return { totalMinutes, entries };
    } catch (error) {
      console.error('Error getting daily aggregated time:', error);
      return { totalMinutes: 0, entries: [] };
    }
  }

  /**
   * Get all manager usernames (for super admin)
   */
  async getAllManagerUsernames(): Promise<string[]> {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser || currentUser.userType !== 'superadmin') {
        return [];
      }

      const response = await fetch(`${API_BASE_URL}/api/managers`);
      const result = await response.json();

      if (!response.ok) {
        console.error('Failed to get managers:', result.error);
        return [];
      }

      return result.data.map((manager: any) => manager.username);
    } catch (error) {
      console.error('Error getting manager usernames:', error);
      return [];
    }
  }

  /**
   * Get all managers with details (for super admin)
   */
  async getAllManagers(): Promise<any[]> {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser || currentUser.userType !== 'superadmin') {
        return [];
      }

      const response = await fetch(`${API_BASE_URL}/api/managers`);
      const result = await response.json();

      if (!response.ok) {
        console.error('Failed to get managers:', result.error);
        return [];
      }

      return result.data;
    } catch (error) {
      console.error('Error getting managers:', error);
      return [];
    }
  }

  /**
   * Log activity (for activity tracking)
   */
  async logActivity(page: string, eventType: string, idleDurationMs?: number): Promise<void> {
    try {
      const user = this.getCurrentUser();
      if (!user || user.userType !== 'manager') {
        return; // Only log activity for managers
      }

      await fetch(`${API_BASE_URL}/api/managers/activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: user.username,
          page,
          eventType,
          idleDurationMs
        })
      });
    } catch (error) {
      console.error('Error logging activity:', error);
      // Don't throw - activity logging shouldn't break the app
    }
  }

  /**
   * Get activity summary for a user and date (super admin only)
   */
  async getActivitySummary(username: string, date: string): Promise<any> {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser || currentUser.userType !== 'superadmin') {
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/api/managers/${username}/activity/${date}`);
      const result = await response.json();

      if (!response.ok) {
        console.error('Failed to get activity summary:', result.error);
        return null;
      }

      return result.data;
    } catch (error) {
      console.error('Error getting activity summary:', error);
      return null;
    }
  }
}

export const authService = new AuthService();
export type { LoginCredentials, AuthUser, TimeEntry }; 